import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { flushOfflineMeals, getPendingMeals, setOfflineMealsUser } from "@/lib/offlineMeals";

// Mounted once at the app root: re-sends offline-queued meals whenever the
// network comes back, the app returns to the foreground, or every 30s while
// something is still waiting.
export default function OfflineMealSync() {
  const { user } = useAuth() || {};
  const { t } = useT();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    setOfflineMealsUser(user?.user_id, () => toast.info(tRef.current("offline.savedOffline"), { id: "offline-meal-saved", duration: 5000 }));
    if (!user || user.role !== "client") return undefined;

    const run = async () => {
      if (getPendingMeals().length === 0) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const { sent, failed } = await flushOfflineMeals();
      if (sent > 0) toast.success(tRef.current("offline.synced", { count: sent }), { id: "offline-meal-sync" });
      if (failed > 0) toast.error(tRef.current("offline.syncFailed", { count: failed }), { id: "offline-meal-fail" });
    };

    run();
    const onVisible = () => { if (document.visibilityState === "visible") run(); };
    window.addEventListener("online", run);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(run, 30000);
    let capListener = null;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener("appStateChange", ({ isActive }) => { if (isActive) run(); }).then((l) => { capListener = l; }).catch(() => {});
    }
    return () => {
      window.removeEventListener("online", run);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
      try { capListener?.remove(); } catch { /* noop */ }
    };
  }, [user]);

  return null;
}
